import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './components/primitives';
import { ApiRequestError } from './lib/api';
// i18n must be initialised before any component calls useTranslation, or the
// first render throws.
import i18n from './i18n';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 4xx means the request itself was wrong — retrying just burns the user's
      // data allowance. Only server and network failures are worth a retry.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * Server data is language-dependent - job categories and skill names are
 * translated by the API - so a cached response is only valid for the language it
 * was fetched in. Several of those queries carry a 30-minute staleTime, which
 * without this would leave a candidate looking at English categories under a
 * Tamil interface for half an hour after switching.
 *
 * Invalidating everything is deliberate: any response may contain translated
 * text, and enumerating which ones do would rot the first time an endpoint
 * started returning some.
 */
i18n.on('languageChanged', () => {
  void queryClient.invalidateQueries();
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
